/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';

import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import './MapLocationPicker.scss';

const configureMapboxToken = (settings) => {
  if (settings?.mapBoxToken && !mapboxgl.accessToken) {
    mapboxgl.accessToken = settings.mapBoxToken;
  }
};

function MapLocationPicker({
  value,
  onChange,
  onLocationSelect,
  disabled = false,
  height = 400,
  settings,
}) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const geocoder = useRef(null);
  const isInitializing = useRef(false);
  const reverseGeocodeTimeout = useRef(null);
  const disabledRef = useRef(disabled); // Ref para valor atual de disabled
  const lastCepRequestedRef = useRef('');
  const lastNoResultQueryRef = useRef('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const CEP_RE = /^\s*\d{5}-?\d{3}\s*$/;

  const extractAddressData = useCallback((location) => {
    const { context = [], address } = location;

    let postalCode = '';
    let street = '';
    let number = '';
    let neighborhood = '';
    let city = '';
    let state = '';
    let country = '';

    context.forEach((item) => {
      // CEP
      if (item.id.includes('postcode')) {
        postalCode = item.text;
      }
      // Cidade
      else if (item.id.includes('place')) {
        city = item.text;
      }
      // Estado
      else if (item.id.includes('region')) {
        state = item.short_code?.split('-')[1] || item.text;
      }
      // País
      else if (item.id.includes('country')) {
        country = item.text;
      }
      // Bairro
      else if (item.id.includes('neighborhood')) {
        neighborhood = item.text;
      }
    });

    // Extrair nome da rua e número
    if (address) {
      const addressParts = address.split(',');
      const firstPart = addressParts[0]?.trim();

      if (firstPart) {
        // Tenta encontrar o número no final da string
        const match = firstPart.match(/^(.*?),?\s+([\d-]+.*)$/);
        // Tenta encontrar o número no início da string
        const startWithNumberMatch = firstPart.match(/^([\d-]+)\s+(.*)/);

        if (match) {
          street = match[1].trim().replace(/,$/, '');
          number = match[2].trim();
        } else if (startWithNumberMatch) {
          number = startWithNumberMatch[1].trim();
          street = startWithNumberMatch[2].trim().replace(/,$/, '');
        } else {
          street = firstPart;
        }
      }
    }

    return {
      postalCodeLogistic: postalCode,
      streetLogistic: street,
      numberLogistic: number,
      districtLogistic: neighborhood,
      cityLogistic: city,
      stateLogistic: state,
      countryLogistic: country,
      coordinates: {
        latitude: location.lat,
        longitude: location.lng,
      },
      fullAddress: address,
    };
  }, []);

  // Manipular seleção de localização (sem useCallback para evitar loops)
  const handleLocationSelect = (location) => {
    // Evitar múltiplas chamadas desnecessárias
    if (
      currentLocation &&
      currentLocation.lng === location.lng &&
      currentLocation.lat === location.lat
    ) {
      return;
    }

    setCurrentLocation(location);

    // Extrair informações do endereço
    const addressData = extractAddressData(location);

    if (onChange) {
      onChange(location);
    }

    if (onLocationSelect) {
      onLocationSelect(addressData);
    }
  };

  // Geocodificação reversa com debounce (sem useCallback para evitar loops)
  const reverseGeocode = (lng, lat) => {
    // Cancelar timeout anterior se existir
    if (reverseGeocodeTimeout.current) {
      clearTimeout(reverseGeocodeTimeout.current);
    }

    // Criar novo timeout para fazer debounce
    reverseGeocodeTimeout.current = setTimeout(async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}&language=pt&country=br`
        );

        if (response.data.features.length > 0) {
          const feature = response.data.features[0];
          handleLocationSelect({
            lng,
            lat,
            address: feature.place_name,
            context: feature.context,
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro na geocodificação reversa:', error);
        message.error('Erro ao buscar informações do endereço');
      } finally {
        setIsLoading(false);
      }
    }, 300); // Debounce de 300ms
  };

  // Definir posição do marcador (sem useCallback para evitar loops)
  const setMarkerPosition = (lng, lat, shouldFly = true) => {
    if (!map.current) {
      return;
    }

    // Remover marcador existente
    if (marker.current) {
      marker.current.remove();
    }

    // Criar novo marcador
    marker.current = new mapboxgl.Marker({
      color: 'red',
      draggable: !disabledRef.current, // Usar ref atualizada
    })
      .setLngLat([lng, lat])
      .addTo(map.current);

    // Evento de drag do marcador
    if (!disabledRef.current) {
      marker.current.on('dragend', () => {
        const lngLat = marker.current.getLngLat();
        reverseGeocode(lngLat.lng, lngLat.lat);
      });
    }

    // Centralizar mapa na posição apenas se necessário
    if (shouldFly) {
      map.current.flyTo({
        center: [lng, lat],
        zoom: 15,
      });
    }

    setCurrentLocation({ lng, lat });
  };

  // Atualizar ref do disabled
  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  // Atualizar draggable do marcador quando disabled muda
  useEffect(() => {
    if (marker.current) {
      marker.current.setDraggable(!disabled);
    }
  }, [disabled]);

  // Inicializar mapa
  useEffect(() => {
    if (map.current || !mapContainer.current || isInitialized || isInitializing.current)
      return undefined;

    isInitializing.current = true;

    configureMapboxToken(settings);

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-46.6333, -23.5505], // São Paulo como centro padrão
      zoom: 10,
      language: 'pt',
      // Garantir que todas as interações básicas estão habilitadas
      dragPan: true,
      dragRotate: true,
      scrollZoom: true,
      boxZoom: true,
      doubleClickZoom: true, // Manter zoom por duplo clique, mas vamos controlar no evento
      touchZoomRotate: true,
      keyboard: true,
    });

    // Adicionar controles de navegação
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Adicionar geocoder
    geocoder.current = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl,
      marker: false,
      language: 'pt-BR',
      countries: 'br',
      types: 'postcode,address,poi,place',
      placeholder: 'Buscar por CEP ou endereço...',
    });

    geocoder.current.on('results', (e) => {
      // Se o Mapbox retornou resultados, não fazer fallback
      if (e?.features && e.features.length) return;

      const inputEl = document.querySelector('.mapboxgl-ctrl-geocoder--input');
      const queryRaw = (inputEl?.value || '').trim();
      if (!queryRaw || !CEP_RE.test(queryRaw)) return;

      const cleanCep = queryRaw.replace(/\D/g, '');
      if (lastCepRequestedRef.current === cleanCep) return;
      lastCepRequestedRef.current = cleanCep;

      axios
        .get(`https://viacep.com.br/ws/${cleanCep}/json/`)
        .then(async ({ data }) => {
          if (!data || data.erro) {
            message.warning('Não foi possível localizar o CEP informado.');
            return;
          }

          const addr = [data.logradouro, data.bairro, `${data.localidade} - ${data.uf}`, data.cep]
            .filter(Boolean)
            .join(', ');

          const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            addr
          )}.json?access_token=${mapboxgl.accessToken}&country=br&language=pt-BR`;
          let mb;
          try {
            mb = await fetch(url).then((r) => r.json());
          } catch (_) {
            message.error('Erro ao buscar localização no mapa.');
            return;
          }

          if (mb?.features?.[0]) {
            const f = mb.features[0];
            const [lng, lat] = f.center;
            setMarkerPosition(lng, lat, true);
            handleLocationSelect({
              lng,
              lat,
              address: f.place_name,
              context: f.context,
            });
            // Limpar input após fallback bem-sucedido
            if (geocoder.current && typeof geocoder.current.clear === 'function') {
              geocoder.current.clear();
            } else if (inputEl) {
              inputEl.value = '';
            }
            lastCepRequestedRef.current = '';
            lastNoResultQueryRef.current = '';
          } else {
            message.warning('Não foi possível localizar o endereço do CEP informado.');
          }
        })
        .catch(() => {
          message.error('Erro ao consultar CEP. Tente novamente.');
        });
    });

    map.current.addControl(geocoder.current, 'top-left');

    // Desabilitar autocomplete do navegador no campo de busca
    map.current.on('load', () => {
      const geocoderInput = document.querySelector('.mapboxgl-ctrl-geocoder--input');
      if (geocoderInput) {
        geocoderInput.setAttribute('autocomplete', 'off');
        geocoderInput.setAttribute('spellcheck', 'false');
        geocoderInput.setAttribute('autocorrect', 'off');
        geocoderInput.setAttribute('autocapitalize', 'off');
      }
    });

    // Evento de duplo clique no mapa para marcar localização
    map.current.on('dblclick', (e) => {
      if (disabledRef.current) {
        return;
      }

      const { lng, lat } = e.lngLat;

      // Marcar a localização - o zoom padrão ainda vai acontecer
      setMarkerPosition(lng, lat, false); // Não fazer fly automático no duplo clique
      reverseGeocode(lng, lat);
    });

    // Evento do geocoder
    geocoder.current.on('result', (e) => {
      if (disabledRef.current) {
        return;
      }

      const { geometry, place_name, context } = e.result;
      const [lng, lat] = geometry.coordinates;

      // Evitar chamadas desnecessárias se a localização não mudou
      if (currentLocation && currentLocation.lng === lng && currentLocation.lat === lat) {
        return;
      }

      setMarkerPosition(lng, lat, true); // Fazer fly no resultado da busca
      handleLocationSelect({
        lng,
        lat,
        address: place_name,
        context,
      });
      lastCepRequestedRef.current = '';
      lastNoResultQueryRef.current = '';
    });

    geocoder.current.on('clear', () => {
      lastCepRequestedRef.current = '';
      lastNoResultQueryRef.current = '';
    });

    setIsInitialized(true);
    isInitializing.current = false;

    // Cleanup
    return () => {
      if (reverseGeocodeTimeout.current) {
        clearTimeout(reverseGeocodeTimeout.current);
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      setIsInitialized(false);
      isInitializing.current = false;
    };
  }, [settings]);

  // Controlar estado disabled do geocoder e mapa
  useEffect(() => {
    if (!map.current || !geocoder.current || !isInitialized) return;

    const geocoderInput = document.querySelector('.mapboxgl-ctrl-geocoder--input');
    if (geocoderInput) {
      geocoderInput.disabled = disabled;
      geocoderInput.style.pointerEvents = disabled ? 'none' : 'auto';
      geocoderInput.style.opacity = disabled ? '0.6' : '1';
    }

    // Desabilitar/habilitar controles de navegação
    const navControls = document.querySelector('.mapboxgl-ctrl-group');
    if (navControls) {
      navControls.style.pointerEvents = disabled ? 'none' : 'auto';
      navControls.style.opacity = disabled ? '0.6' : '1';
    }

    // Controlar interações do mapa
    if (disabled) {
      map.current.dragPan.disable();
      map.current.scrollZoom.disable();
      map.current.boxZoom.disable();
      map.current.doubleClickZoom.disable();
      map.current.dragRotate.disable();
      map.current.keyboard.disable();
      map.current.touchZoomRotate.disable();
    } else {
      map.current.dragPan.enable();
      map.current.scrollZoom.enable();
      map.current.boxZoom.enable();
      map.current.doubleClickZoom.enable();
      map.current.dragRotate.enable();
      map.current.keyboard.enable();
      map.current.touchZoomRotate.enable();
    }
  }, [disabled, isInitialized]);

  // Definir valor inicial
  useEffect(() => {
    if (value && value.lng && value.lat && map.current && isInitialized) {
      setMarkerPosition(value.lng, value.lat, true);
    }
  }, [value, isInitialized]);

  return (
    <div className="map-location-picker-field">
      <div
        ref={mapContainer}
        className={`map-container ${disabled ? 'disabled' : ''}`}
        style={{
          height: `${height}px`,
          borderRadius: '6px',
          overflow: 'hidden',
          border: '1px solid #d9d9d9',
          opacity: disabled ? 0.6 : 1,
        }}
      />

      {isLoading && (
        <div className="map-loading-overlay">
          <span>Buscando informações do endereço...</span>
        </div>
      )}
    </div>
  );
}

export default MapLocationPicker;
